import Button from '@atoms/Button/Button';
import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import { IPromoBanner } from '@models/IPromoBanner';
import BreakpointTypeEnum from '@models/enums/BreakpointTypeEnum';
import CrossSellProductCTAStatusEnum from '@models/enums/CrossSellProductCTAStatusEnum';
import CrossSellProductCard from '@molecules/CrossSellProductCard/CrossSellProductCard';
import NavigationCarousel from '@molecules/NavigationCarousel/NavigationCarousel';
import TabNavigation from '@molecules/TabNavigation/TabNavigation';
import updateDataLayer from '@utils/helpers/gtm';
import slugify from '@utils/helpers/slugify';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import useTabbedProductsCTAStatuses from '@utils/hooks/useTabbedProductsCTAStatuses';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { SwiperSlide } from 'swiper/react';

interface Props extends IPromoBanner {
  readonly isInTray?: boolean;
  readonly onAddItemToCart: (
    onSuccessfulAddToCart: () => void
  ) => (code: string, quantity: number) => void;
  readonly onRemoveItemFromCart: (
    onSuccessfulRemoveFromCart: () => void
  ) => (code: string) => void;
  readonly onProductsSectionDisplayed?: (
    isProductsSectionDisplayed: boolean
  ) => void;
}

const PromoBanner = ({
  addedLabel,
  addToBasketLabel,
  ctaText,
  description,
  desktopBackgroundImage,
  headline,
  isDiscountApplied,
  isInTray,
  expandOnLoad,
  mobileBackgroundImage,
  productGroups,
  removeLabel,
  trayLink,
  trayTitleApplied,
  trayTitlePending,
  trayTitlePendingRemainingAmount,
  onAddItemToCart,
  onRemoveItemFromCart,
  onProductsSectionDisplayed,
}: Props) => {
  const isMobileScreen = useMediaQuery(1024, BreakpointTypeEnum.Screen);
  const isMobileViewport = useMediaQuery(1024);
  const isSmallDesktopScreen = useMediaQuery(1440, BreakpointTypeEnum.Screen);
  const isSmallDesktopViewport = useMediaQuery(1440);
  const [isPromoProductsSectionDisplayed, setIsPromoProductsSectionDisplayed] =
    useState(isDiscountApplied || expandOnLoad);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [recentlyAddedProducts, setRecentlyAddedProducts] = useState<
    Set<string>
  >(new Set());
  const displayedTabs = useMemo(
    () => productGroups.filter((tab) => tab.items.length > 0),
    [productGroups]
  );
  const activeTabItems = displayedTabs[activeTabIndex]?.items || [];
  const isCarouselDisplayed =
    (isMobileScreen && isInTray) || (isSmallDesktopScreen && !isInTray);
  const hasTabNavigationUnderline =
    (isMobileViewport && isInTray) || (isSmallDesktopViewport && !isInTray);

  const { itemsCTAStatuses, handleUpdateItemCTAStatus } =
    useTabbedProductsCTAStatuses(displayedTabs);

  // Stores the added sku in temporary state, so we can show the "Added" label for 2 seconds.
  const showAddedStatus = (sku: string) => {
    setRecentlyAddedProducts((currentProducts) => {
      const nextProducts = new Set(currentProducts);
      nextProducts.add(sku);
      return nextProducts;
    });

    // Clear after 2 seconds, when the "Added" label disappears.
    setTimeout(() => {
      setRecentlyAddedProducts((currentProducts) => {
        const nextProducts = new Set(currentProducts);
        nextProducts.delete(sku);
        return nextProducts;
      });
    }, 2000);
  };

  const handleSuccessfulAddItemToCart = (sku: string) => () =>
    showAddedStatus(sku);

  useEffect(() => {
    onProductsSectionDisplayed?.(isPromoProductsSectionDisplayed);
  }, [isPromoProductsSectionDisplayed, onProductsSectionDisplayed]);

  const handleSuccessfulRemoveItemFromCart = (index: number) => () => {
    handleUpdateItemCTAStatus(
      activeTabIndex,
      index,
      CrossSellProductCTAStatusEnum.AddToBasket
    );
  };

  const handleViewAccessoriesButtonClick = () => {
    setIsPromoProductsSectionDisplayed(true);

    updateDataLayer({
      event: 'selectContent',
      content_type: 'promo_banner',
      link_text: ctaText,
    });
  };

  const tabsContainer =
    displayedTabs.length > 1 ? (
      <div
        data-testid="CT-170-Promo-Banner-Products-Section-Tab-Navigation"
        className="min-w-0"
      >
        <TabNavigation
          activeTabId={`${activeTabIndex}`}
          onTabChange={(id, title) => {
            setActiveTabIndex(Number(id));

            updateDataLayer({
              event: 'selectContent',
              content_type: 'promo_banner_tab',
              link_text: title,
            });
          }}
          tabs={displayedTabs.map((tab, index) => ({
            id: `${index}`,
            title: tab.title,
          }))}
          tabsSpacingClassName="gap-x-6"
          tabTitleTypeClassName="body-one"
          activeTabTitleClassName="body-one--bold"
          hasContainerUnderline={hasTabNavigationUnderline}
          underlineClassName={classNames(
            'h-0.5 bg-black-100',
            hasTabNavigationUnderline ? 'mt-2.5' : 'mt-0.5'
          )}
          shadowColour="from-grey-100"
          dataTestId="CT-170-Promo-Banner-Products-Section-Tab-Navigation"
        />
      </div>
    ) : undefined;

  return (
    <section data-testid="CT-170-Promo-Banner">
      <AnimatePresence mode="wait">
        {isPromoProductsSectionDisplayed ? (
          <motion.div
            key="productsSection"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden rounded-[10px]"
            data-testid="CT-170-Promo-Banner-Products-Section"
          >
            <div
              className={classNames(
                'flex items-start bg-yellow p-5',
                isInTray
                  ? 'max-md:flex-col max-md:gap-y-4 md:items-center md:justify-between'
                  : 'max-lg:flex-col max-lg:gap-y-4 lg:items-center lg:justify-between'
              )}
            >
              <div className="flex items-center gap-x-4">
                <Icon
                  name={isDiscountApplied ? 'check_circle' : 'label'}
                  className="shrink-0"
                />

                <p data-testid="CT-170-Promo-Banner-Products-Section-Title">
                  {isDiscountApplied ? (
                    trayTitleApplied
                  ) : (
                    <>
                      {trayTitlePending.split('{0}')[0]}
                      <span className="font-normal">
                        {trayTitlePendingRemainingAmount}
                      </span>
                      {trayTitlePending.split('{0}')[1]}
                    </>
                  )}
                </p>
              </div>

              {trayLink?.url && trayLink.name && (
                <a
                  href={trayLink.url}
                  target={trayLink.target}
                  className="shrink-0 font-normal underline"
                  data-testid="CT-170-Promo-Banner-Products-Section-Link"
                >
                  {trayLink.name}
                </a>
              )}
            </div>

            <div
              className={classNames(
                'bg-grey-100',
                isInTray ? 'max-lg:py-5 lg:p-5' : 'max-xl:py-5 xl:p-5'
              )}
            >
              {(isInTray || !isCarouselDisplayed) && tabsContainer && (
                <div
                  className={classNames(
                    'mb-4',
                    isInTray ? 'max-lg:px-5 lg:mb-5' : 'max-xl:px-5 xl:mb-5'
                  )}
                >
                  {tabsContainer}
                </div>
              )}

              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTabIndex}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {isCarouselDisplayed ? (
                    <NavigationCarousel
                      id="promo-banner-products-carousel"
                      dataTestId="CT-170-Promo-Banner-Products-Section-Carousel"
                      items={activeTabItems}
                      loop={false}
                      headerSlot={
                        !isInTray && tabsContainer ? tabsContainer : undefined
                      }
                      headerWrapperClassName="gap-x-5 min-h-[2.5rem]"
                      headerHorizontalSpacingClassName="px-5"
                      carouselHorizontalSpacingClassName="px-5"
                      shadows
                      shadowColour="from-grey-100"
                      spaceBetween={20}
                      slidesToShowTablet={2.1}
                      additionalBreakpoints={
                        isInTray
                          ? {
                              480: {
                                slidesPerView: 1.5,
                              },
                              600: {
                                slidesPerView: 2.1,
                              },
                            }
                          : {
                              1024: {
                                slidesPerView: 1.6,
                              },
                              1280: {
                                slidesPerView: 2.1,
                              },
                            }
                      }
                      enableDomObserver
                      isLightMode
                    >
                      {(itemsToDisplay) =>
                        itemsToDisplay.map((item, index) => (
                          <SwiperSlide
                            key={slugify(`${item.name}-${index}`)}
                            className="stretch-card"
                          >
                            <div className="h-full">
                              <CrossSellProductCard
                                {...item}
                                dataTestId={`Product-Card-Tab-${activeTabIndex}-Item-${index}`}
                                ctaStatus={
                                  recentlyAddedProducts.has(item.sku ?? '')
                                    ? CrossSellProductCTAStatusEnum.Added
                                    : (itemsCTAStatuses[activeTabIndex]?.[
                                        index
                                      ] ??
                                      CrossSellProductCTAStatusEnum.AddToBasket)
                                }
                                addToBasketLabel={addToBasketLabel}
                                addedLabel={addedLabel}
                                removeLabel={removeLabel}
                                hasFullWidthCta={false}
                                isLightMode
                                nameClassName="px-7"
                                onAddToCart={(code) => {
                                  onAddItemToCart(
                                    handleSuccessfulAddItemToCart(code)
                                  )(code, 1);
                                }}
                                onRemoveFromCart={(code) => {
                                  onRemoveItemFromCart(
                                    handleSuccessfulRemoveItemFromCart(index)
                                  )(code);
                                }}
                              />
                            </div>
                          </SwiperSlide>
                        ))
                      }
                    </NavigationCarousel>
                  ) : (
                    <div
                      className={classNames(
                        'grid grid-cols-1 gap-5 md:grid-cols-2',
                        isInTray
                          ? 'max-lg:px-5 lg:grid-cols-3'
                          : 'max-xl:px-5 xl:grid-cols-3'
                      )}
                      data-testid="CT-170-Promo-Banner-Products-Section-Grid"
                    >
                      {activeTabItems.map((product, index) => (
                        <CrossSellProductCard
                          {...product}
                          key={slugify(`${product.name}-${index}`)}
                          dataTestId={`Product-Card-Tab-${activeTabIndex}-Item-${index}`}
                          ctaStatus={
                            recentlyAddedProducts.has(product.sku ?? '')
                              ? CrossSellProductCTAStatusEnum.Added
                              : (itemsCTAStatuses[activeTabIndex]?.[index] ??
                                CrossSellProductCTAStatusEnum.AddToBasket)
                          }
                          addToBasketLabel={addToBasketLabel}
                          addedLabel={addedLabel}
                          removeLabel={removeLabel}
                          hasFullWidthCta={false}
                          isLightMode
                          nameClassName="px-12"
                          onAddToCart={(code) => {
                            onAddItemToCart(
                              handleSuccessfulAddItemToCart(code)
                            )(code, 1);
                          }}
                          onRemoveFromCart={(code) => {
                            onRemoveItemFromCart(
                              handleSuccessfulRemoveItemFromCart(index)
                            )(code);
                          }}
                        />
                      ))}
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="initialBanner"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={classNames(
              'relative overflow-hidden rounded-[10px]',
              isInTray ? 'lg:p-10' : 'xl:p-10'
            )}
            data-testid="CT-170-Promo-Banner-Initial-Banner"
          >
            <Image
              image={desktopBackgroundImage}
              classes={classNames(
                'absolute inset-0 z-0',
                isInTray ? 'max-lg:hidden' : 'max-xl:hidden'
              )}
              imageClasses="object-cover w-full h-full object-right"
              dataTestId="CT-170-Promo-Banner-Initial-Banner-Desktop-Background-Image"
            />

            <div
              className={classNames(
                'relative',
                isInTray
                  ? 'max-lg:bg-black max-lg:px-5 max-lg:py-7.5 max-lg:text-white lg:w-3/4 lg:text-black-100'
                  : 'max-xl:bg-black max-xl:px-5 max-xl:py-7.5 max-xl:text-white xl:w-1/2 xl:text-black-100'
              )}
            >
              <p
                className="h6 mb-2.5"
                data-testid="CT-170-Promo-Banner-Initial-Banner-Headline"
              >
                {headline}
              </p>

              <p
                className="body-four mb-5"
                data-testid="CT-170-Promo-Banner-Initial-Banner-Description"
              >
                {description}
              </p>

              <div className={isInTray ? 'max-lg:hidden' : 'max-xl:hidden'}>
                <Button
                  onClick={() => {
                    handleViewAccessoriesButtonClick();
                  }}
                  hasCustomStyling
                  className="bg-black-100 px-4 py-1 text-transparent text-yellow hover:bg-[#333333] focus:bg-[#333333] active:bg-[#333333]"
                  dataTestId="CT-170-Promo-Banner-Initial-Banner-CTA-Desktop"
                >
                  {ctaText}
                </Button>
              </div>

              <div className={isInTray ? 'lg:hidden' : 'xl:hidden'}>
                <Button
                  onClick={() => {
                    handleViewAccessoriesButtonClick();
                  }}
                  dataTestId="CT-170-Promo-Banner-Initial-Banner-CTA-Mobile"
                >
                  {ctaText}
                </Button>
              </div>
            </div>

            <Image
              image={mobileBackgroundImage}
              classes={isInTray ? 'lg:hidden' : 'xl:hidden'}
              imageClasses="object-cover w-full h-[242px] md:h-[262px]"
              dataTestId="CT-170-Promo-Banner-Initial-Banner-Mobile-Background-Image"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default PromoBanner;
